import { ColorType, useTheme } from "./AHTheme/AHTheme";
import { IconSize, useLayoutContext } from "./LayoutProvider";

import { BoxProps } from "./AHTheme/BoxProps";
import { MaterialCommunityIcons, Ionicons, Entypo } from "@expo/vector-icons";
import React from "react";
import { TeamIcon } from "../globalTypes";
import { useBoxStyle } from "./AHTheme/useBoxStyle";
import { useScaleFactor } from "./AHScaleFactor";
import { useFragment, graphql } from "react-relay";
import { AHTeamIcon$key } from "./__generated__/AHTeamIcon.graphql";

function asertNever(_: never) {
  console.warn("No implementation for todo category icon");
}

type TeamIconProps = {
  type: TeamIcon | null | keyof typeof TeamIcon;
  size?: IconSize;
  color?: ColorType;
  customColor?: string;
} & Omit<BoxProps, "color">;

export function AHTeamIcon(props: TeamIconProps) {
  const { color: c, size: s, customColor, ...boxProps } = props;
  const boxStyle = useBoxStyle(boxProps);
  const theme = useTheme();
  const layout = useLayoutContext();
  const color = customColor ?? theme[c ?? "darkerText"];
  const sf = useScaleFactor();
  const size = layout.icon[s ?? "small"] * sf;

  const type = TeamIcon[props.type ?? TeamIcon.default];
  switch (type) {
    case TeamIcon.account_hard_hat:
      return (
        <MaterialCommunityIcons
          name="account-hard-hat"
          color={color}
          size={size}
          style={boxStyle}
        />
      );
    case TeamIcon.bread_slice:
      return (
        <MaterialCommunityIcons
          style={boxStyle}
          name="bread-slice"
          color={color}
          size={size}
        />
      );
    case TeamIcon.account_tie:
      return (
        <MaterialCommunityIcons
          style={boxStyle}
          name="account-tie"
          color={color}
          size={size}
        />
      );
    case TeamIcon.baby_carriage:
      return (
        <MaterialCommunityIcons
          name="baby-carriage"
          color={color}
          size={size}
          style={boxStyle}
        />
      );
    case TeamIcon.cash_register: {
      return (
        <MaterialCommunityIcons
          name="cash-register"
          color={color}
          size={size}
          style={boxStyle}
        />
      );
    }
    case TeamIcon.fork_knife:
      return (
        <MaterialCommunityIcons
          name="silverware-fork-knife"
          color={color}
          size={size}
          style={boxStyle}
        />
      );
    case TeamIcon.pineapple:
      return (
        <MaterialCommunityIcons
          name="fruit-pineapple"
          color={color}
          size={size}
          style={boxStyle}
        />
      );
    case TeamIcon.watermelon:
      return (
        <MaterialCommunityIcons
          name="fruit-watermelon"
          color={color}
          size={size}
          style={boxStyle}
        />
      );
    case TeamIcon.tshirt:
      return (
        <MaterialCommunityIcons
          name="tshirt-crew"
          color={color}
          size={size}
          style={boxStyle}
        />
      );
    case TeamIcon.milk:
      return (
        <MaterialCommunityIcons
          name="beer"
          color={color}
          size={size}
          style={boxStyle}
        />
      );
    case TeamIcon.steak:
      return (
        <MaterialCommunityIcons
          name="food-steak"
          color={color}
          size={size}
          style={boxStyle}
        />
      );
    case TeamIcon.soda:
      return (
        <MaterialCommunityIcons
          name="bottle-soda"
          color={color}
          size={size}
          style={boxStyle}
        />
      );
    case TeamIcon.hammer_wrench:
      return (
        <MaterialCommunityIcons
          style={boxStyle}
          name="hammer-wrench"
          color={color}
          size={size}
        />
      );
    case TeamIcon.meat:
      return (
        <MaterialCommunityIcons
          style={boxStyle}
          name="food-drumstick"
          color={color}
          size={size}
        />
      );
    case TeamIcon.cash:
      return (
        <MaterialCommunityIcons
          name="cash-100"
          color={color}
          size={size}
          style={boxStyle}
        />
      );
    case TeamIcon.coffe:
      return (
        <MaterialCommunityIcons
          name="coffee"
          color={color}
          size={size}
          style={boxStyle}
        />
      );
    case TeamIcon.default:
      return (
        <MaterialCommunityIcons
          style={boxStyle}
          name="account-group"
          color={color}
          size={size}
        />
      );
    case TeamIcon.apple:
      return (
        <MaterialCommunityIcons
          style={boxStyle}
          name="food-apple"
          color={color}
          size={size}
        />
      );
    case TeamIcon.sofa:
      return (
        <MaterialCommunityIcons
          style={boxStyle}
          name="sofa"
          color={color}
          size={size}
        />
      );
    case TeamIcon.tv:
      return (
        <Ionicons
          style={boxStyle}
          name="ios-tv-sharp"
          color={color}
          size={size}
        />
      );
    case TeamIcon.laptop:
      return (
        <Entypo style={boxStyle} name="laptop" color={color} size={size} />
      );
    case TeamIcon.desk_lamp:
      return (
        <MaterialCommunityIcons
          style={boxStyle}
          name="desk-lamp"
          color={color}
          size={size}
        />
      );
    case TeamIcon.bed:
      return (
        <MaterialCommunityIcons
          style={boxStyle}
          name="bed"
          color={color}
          size={size}
        />
      );
    case TeamIcon.market:
      return (
        <MaterialCommunityIcons
          style={boxStyle}
          name="store"
          color={color}
          size={size}
        />
      );
    case TeamIcon.broom:
    case TeamIcon.carrot:
    case TeamIcon.cart:
    case TeamIcon.cheese:
    case TeamIcon.cookie:
    case TeamIcon.cupcake:
    case TeamIcon.fish:
    case TeamIcon.food:
    case TeamIcon.hamburger:
    case TeamIcon.flower:
    case TeamIcon.fridge:
    case TeamIcon.dolly:
    case TeamIcon.forklift:
    case TeamIcon.basket:
    case TeamIcon.baguette:
      return (
        <MaterialCommunityIcons
          style={boxStyle}
          name={type}
          color={color}
          size={size}
        />
      );
    default:
      asertNever(type);
      return null;
  }
}

export function AHTeamIconRelay(
  props: Omit<TeamIconProps, "type"> & {
    team: AHTeamIcon$key;
  }
) {
  const { icon } = useFragment(
    graphql`
      fragment AHTeamIcon on Team {
        icon
      }
    `,
    props.team
  );

  return <AHTeamIcon {...props} type={icon} />;
}
